import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronDown } from 'lucide-react'
import TestimonialCard, { Testimonial } from '../components/ui/TestimonialCard'
import AnimatedCounter from '../components/ui/AnimatedCounter'
import Seo from '../components/Seo'

const Home: React.FC = () => {
  const scrollToNextSection = () => {
    const target = document.getElementById('credentials-section')
    if (!target) return
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
  }

  const services = [
    {
      title: 'The Strong Again Method™',
      description: 'I want to get stronger and change my body.',
      link: '/private-training',
      image: '/images/cassie-private-path.webp',
      alt: 'Cassie coaching strength training',
      tag: 'Strength + Body',
      objectClass: 'object-[center_22%]'
    },
    {
      title: 'Nutrition Blueprint',
      description: 'I need help getting my nutrition together.',
      link: '/meal-plan',
      image: '/images/meal-prep-hero.webp',
      alt: 'Cassie preparing healthy nutrition in the kitchen',
      tag: 'Nutrition',
      objectClass: 'object-[center_32%]'
    },
    {
      title: 'Physique & Competition Coaching',
      description: 'I want to compete or develop my physique at a higher level.',
      link: '/online-training',
      image: '/images/cassie-online-path.webp',
      alt: 'Cassie representing advanced physique and competition coaching',
      tag: 'Physique + Competition',
      objectClass: 'object-[left_center]'
    }
  ]

  const steps = [
    {
      number: '01',
      title: 'Schedule Your Complimentary Consultation',
      description:
        'We’ll talk about your goals, your experience, your schedule, and what you’re looking for. It’s a chance to see whether we’re a good fit.'
    },
    {
      number: '02',
      title: 'Build Your Individual Plan',
      description:
        'Once you’re enrolled and I’ve reviewed your information, I’ll prepare the training, nutrition, or physique coaching guidance included in your program.'
    },
    {
      number: '03',
      title: 'Make Progress With Support',
      description:
        'We’ll work together through the coaching support included in your program, with feedback and adjustments as your needs and progress change.'
    }
  ]

  const testimonials: Testimonial[] = [
    {
      name: 'Priscilla',
      content:
        'Choosing Cassie as my coach has truly been life-changing. I’ve trained for many years and worked with several personal trainers, and none have matched her expertise. She genuinely knows what she’s doing, and you won’t regret trusting her with your goals. Results take time, but you’ll notice a real difference even within the first month of training. She understands weight-resistance training and nutrition at a high level, and taught me how to make the right food choices. You will not find a more dedicated, educated, and driven coach anywhere.',
      rating: 5
    },
    {
      name: 'Grace',
      content:
        'Empathy, knowledge, and accountability are the top qualities I look for in a coach, and Cassie has exceeded my expectations in all of them. As my first coach, she has checked every box and it has been an honor to be her client. She has taught me so much and kept me accountable with weekly check-ins and nutrition adjustments that fit my busy lifestyle. I have made more progress with her than I ever did working with doctors or nutritionists.\nThank you, Cassie, for your patience and for answering all of my questions. With so much information out there, it has brought me peace of mind to know I have someone I can trust guiding me on my journey.',
      rating: 5
    },
    {
      name: 'Angel',
      content:
        'Training with Cassie has truly changed my life. She transformed my body and mind and got me into the gym on a consistent basis. Her positive energy, motivation, and encouragement keep you focused and committed to results.\nShe continually pushes you beyond your comfort zone so you improve with every session and achieve the results you want. On top of that, she is kind, outgoing, and a joy to be around.',
      rating: 5
    },
    {
      name: 'Celina',
      content:
        'Cassie is truly one of the best coaches I’ve ever worked with. I’ve tried other coaches and followed very strict diets, but I never saw the kind of results I achieved with her. She is incredibly sweet, supportive, and by my side through every step of the process. Cassie was my number one source of motivation in every way, and I’m so grateful for her guidance. Thank you for being the best coach!',
      rating: 5
    },
    {
      name: 'Jessica',
      content:
        'Cassie, I loved my experience with you as my coach and trainer when I started bodybuilding at 33 years old. You gave me confidence not just physically but mentally, because you always push your clients to reach their full potential. You genuinely care about your clients, and that is what makes you unique.\nTraining with you was a dream come true. You helped me overcome many challenges and insecurities. Anyone would be lucky to have you as their trainer. I highly recommend your services because your kindness, patience, and passion for your work inspire others to achieve their fitness goals.',
      rating: 5
    },
    {
      name: 'Janet',
      content:
        'Cassie is the real deal. She genuinely cares about every client and invests the time to make each plan truly individualized. She shared her years of knowledge with me, listened to my needs, and adjusted my program so it fit my life. Cassie went beyond just writing a nutrition plan—she encouraged and motivated me, checked in when things were hard, and always made me feel like I was her only client. No question was ever too small, and she always responded quickly.\nThis was my first time using macros and I was nervous about how to track everything, but Cassie patiently walked me through the process and answered all my questions. I loved having a coach I could look up to—she lives the lifestyle she teaches, doing the same workouts and following the same style of nutrition she gives her clients. I also appreciated the high standards she sets: weekly check-ins, consistent updates, and a strong focus on commitment to the process. Cassie helped me build sustainable healthy habits that I now use in every part of my life; it was never just about hitting a number on the scale.',
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Personal Trainer in the DMV — DC, Maryland & Virginia"
        description="Transform your body with Cassie — NASM certified personal trainer and IFBB Pro. Private 1-on-1 training across the DMV, online coaching, and custom meal plans. Book a free consultation."
        image="/images/cassie-hero-navy.webp"
      />

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[85vh] sm:min-h-[92vh] flex items-center bg-brand-navy">
        <div
          className="absolute inset-0 bg-cover bg-center animate-hero-zoom"
          style={{
            backgroundImage: "url('/images/cassie-hero-navy.webp')",
            backgroundPosition: '72% 20%'
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/75 to-brand-navy/25 sm:from-brand-navy/90 sm:via-brand-navy/60 sm:to-brand-navy/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-transparent to-brand-navy/35" />

        <div className="container-max relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <motion.p
                className="text-xs sm:text-sm font-semibold uppercase tracking-widest-xl text-brand-teal mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                IFBB Bikini Pro · Certified Fitness & Nutrition Coach
              </motion.p>

              <motion.h1
                className="font-display text-4xl sm:text-5xl md:text-7xl font-semibold mb-5 sm:mb-7 leading-[1.05] text-brand-ivory"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Strong enough to keep saying{' '}
                <em className="italic text-brand-blush">yes to life.</em>
              </motion.h1>

              <motion.p
                className="text-base sm:text-lg md:text-xl text-brand-cream/80 mb-3 max-w-xl leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Personalized strength, nutrition, and physique coaching for women
                in their mid-30s and beyond—built around your body, your goals,
                and your actual life.
              </motion.p>

              <motion.p
                className="text-sm text-brand-cream/60 mb-8 sm:mb-10 max-w-xl leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Online coaching is available wherever you are, with select
                in-person sessions in the DMV area offered as an optional
                add-on, subject to availability.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center mb-10 sm:mb-14"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-semibold text-lg px-9 py-4 hover:bg-brand-ivory transition-colors duration-300"
                >
                  Schedule Your Complimentary Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Link>

                <a
                  href="#three-doors"
                  className="inline-flex items-center justify-center rounded-full border border-brand-cream/50 text-brand-cream font-semibold text-lg px-9 py-4 hover:bg-brand-cream/10 transition-colors duration-300"
                >
                  Explore Coaching
                </a>
              </motion.div>

              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-3 md:gap-y-4 max-w-xl md:max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                {[
                  'Strength',
                  'Nutrition',
                  'Wellness',
                  'A Fuller Life'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 min-w-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-bronze shrink-0" />
                    <span className="text-[11px] sm:text-xs md:text-sm font-medium tracking-[0.12em] sm:tracking-[0.16em] text-brand-cream/75 uppercase whitespace-nowrap">
                      {item}
                    </span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={scrollToNextSection}
          aria-label="Scroll to the next section"
          className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cream/70 rounded-lg"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-widest-xl text-brand-cream/70">
            Scroll
          </span>
          <div className="relative w-7 h-11 rounded-full border border-brand-cream/40 bg-brand-cream/5 backdrop-blur-sm flex justify-center pt-2.5">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-bronze motion-safe:animate-scroll-dot" />
          </div>
          <ChevronDown
            size={14}
            className="text-brand-cream/70 motion-safe:animate-bounce"
            aria-hidden
          />
        </motion.button>
      </section>

      {/* Credentials */}
      <section id="credentials-section" className="scroll-mt-28 bg-brand-cream border-y border-brand-bronze/15">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x sm:divide-brand-bronze/20 max-w-2xl mx-auto">
            <div className="text-center px-2 sm:px-6">
              <AnimatedCounter
                end={20}
                suffix="+"
                className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy"
              />
              <div className="mt-2 text-[11px] sm:text-xs font-semibold uppercase tracking-widest-xl text-brand-teal">
                Years of Personal Fitness Experience
              </div>
            </div>

            <div className="text-center px-2 sm:px-6">
              <div className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy">
                IFBB Pro
              </div>
              <div className="mt-2 text-[11px] sm:text-xs font-semibold uppercase tracking-widest-xl text-brand-teal">
                Bikini Professional
              </div>
            </div>

            <div className="text-center px-2 sm:px-6">
              <AnimatedCounter
                end={5}
                className="font-display text-3xl sm:text-4xl font-semibold text-brand-navy"
              />
              <div className="mt-2 text-[11px] sm:text-xs font-semibold uppercase tracking-widest-xl text-brand-teal">
                Professional Certifications
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Starting Over Assessment */}
      <section className="py-16 sm:py-20 bg-brand-navy">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-4">
              Free Starting-Over Assessment
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-ivory leading-tight mb-6">
              Not sure where to start?
              <br />
              <em className="italic text-brand-blush">
                Find your starting point.
              </em>
            </h2>

            <p className="text-lg text-brand-cream/70 max-w-2xl mx-auto leading-relaxed mb-4">
              Take the six-question assessment to discover which Starting-Over
              story best reflects where you are right now.
            </p>

            <p className="text-brand-cream/55 max-w-xl mx-auto leading-relaxed mb-9">
              Your starting point is information. It isn't a qualification test.
            </p>

            <Link
              to="/starting-over-guide"
              className="inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-semibold text-lg px-9 py-4 hover:bg-brand-ivory transition-colors duration-300"
            >
              Take the Free Assessment
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Three Doors */}
      <section id="three-doors" className="scroll-mt-28 py-16 sm:py-20 lg:py-24 bg-brand-ivory">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
  <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-teal mb-4">
    Choose Your Coaching Path
  </span>

  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
    Three ways to{' '}
    <em className="italic text-brand-teal">
      work together.
    </em>
  </h2>

  <p className="text-lg text-brand-navy/70 max-w-2xl mx-auto">
    Different goals. Different starting points. Coaching built around you.
  </p>
</motion.div>            

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Link
                  to={service.link}
                  className="relative overflow-hidden rounded-3xl group block aspect-[4/3] sm:aspect-[3/4] shadow-soft-lg bg-brand-navy"
                >
                  <img
                    src={service.image}
                    alt={service.alt}
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${service.objectClass}`}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/95 via-brand-navy/50 to-transparent" />

                  <span className="absolute top-4 left-4 sm:top-5 sm:left-5 inline-flex items-center px-3.5 py-1.5 rounded-full bg-brand-ivory/95 backdrop-blur-sm text-xs font-semibold text-brand-teal uppercase tracking-wider shadow">
                    {service.tag}
                  </span>

                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-brand-ivory mb-2">
                      {service.title}
                    </h3>

                    <p className="text-brand-cream/75 text-sm leading-relaxed mb-3 sm:mb-4">
                      {service.description}
                    </p>

                    <span className="inline-flex items-center text-brand-blush font-semibold text-sm tracking-wide group-hover:translate-x-2 transform transition-transform duration-300">
                      Explore
                      <ArrowRight className="ml-2" size={15} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story band */}
      <section className="py-16 sm:py-20 bg-ink text-white">
        <div className="container-max px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-4">
              The Story Behind Fit by Cassie
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-6 leading-snug">
              I sat in a gym parking lot for twenty-three minutes before I could open the door.
            </h2>
            <p className="text-brand-cream/80 leading-relaxed mb-4">
              I was twenty-two. I wanted to feel stronger and more confident, but I was convinced everyone inside would know I didn't belong.
            </p>
            <p className="text-brand-cream/80 leading-relaxed mb-4">
              I went in anyway.
            </p>
            <p className="text-brand-cream/80 leading-relaxed mb-4">
              That first step didn't change everything overnight. But it gave me something I hadn't had before: a reason to believe I could keep showing up.
            </p>
            <p className="text-brand-cream/80 leading-relaxed mb-8">
              It's one of the reasons I coach the way I do today. I don't expect you to arrive confident, experienced, or with everything figured out. I want to understand where you're starting and help you build from there.
            </p>
            <p className="text-brand-cream/80 leading-relaxed mb-8">
              I've shared that story, along with the Prologue from my memoir, in a free reading called <em>The First Step</em>.
            </p>
            <Link
              to="/coming-home-to-me"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-teal text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Read The First Step — Free
              <ArrowRight className="ml-1" size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 lg:py-24 bg-brand-cream">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-teal mb-4">
                How It Works
              </span>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
                Three steps to your{' '}
                <em className="italic text-brand-teal">strongest self</em>
              </h2>

              <p className="text-lg text-brand-navy/70 mb-10 leading-relaxed">
                No cookie-cutter plans. No expectation that your life has to be
                perfect. Just coaching built around your goals, your starting
                point, and what you can realistically sustain.
              </p>

              <div className="space-y-8">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-6">
                    <span className="font-display text-3xl font-semibold text-brand-bronze shrink-0 leading-none pt-1">
                      {step.number}
                    </span>

                    <div>
                      <h3 className="text-lg font-bold text-brand-navy mb-1.5">
                        {step.title}
                      </h3>
                      <p className="text-brand-navy/65 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/book-consultation"
                className="inline-flex items-center justify-center mt-10 rounded-full bg-brand-navy text-brand-cream font-semibold px-7 py-3.5 hover:bg-brand-teal transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-teal"
              >
                Schedule Your Complimentary Consultation
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-soft-lg bg-brand-ivory">
                <img
                  src="/images/journey-illustration.webp"
                  alt="Fit by Cassie coaching journey: Clarity, Strength, and A Fuller Life"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24 bg-brand-navy">
        <img
          src="/images/hero-gym-bg.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-brand-navy/85" />

        <div className="relative container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-teal mb-4">
              Client Stories
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-brand-ivory mb-6">
              Real women.{' '}
              <em className="italic text-brand-blush">
                Real experiences.
              </em>
            </h2>

            <p className="text-lg text-brand-cream/65 max-w-2xl mx-auto">
              Every woman's starting point is different. Here are a few words from women who've experienced my coaching firsthand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="h-full"
              >
                <TestimonialCard testimonial={testimonial} variant="dark" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-brand-navy">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[280px] sm:min-h-[320px] lg:min-h-0">
            <img
              src="/images/cassie-cta.webp"
              alt="Cassie coaching a client through an overhead dumbbell press"
              className="absolute inset-0 w-full h-full object-cover object-[center_48%]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-brand-navy/95 hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/95 to-transparent lg:hidden" />
          </div>

          <div className="relative px-6 sm:px-12 lg:px-16 py-20 lg:py-28 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-teal mb-5">
                Your Turn
              </span>

              <h2 className="font-display text-3xl lg:text-5xl font-semibold leading-tight mb-6 text-brand-ivory">
                Ready to become your
                <em className="italic text-brand-blush"> strongest self?</em>
              </h2>

              <p className="text-lg text-brand-cream/65 mb-9 max-w-lg leading-relaxed">
                You don't have to have everything figured out before we talk.
                We'll start with your goals, your starting point, and what you
                want your life to look like. Then we'll see which coaching path
                makes sense for you.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-semibold text-lg px-8 py-4 hover:bg-brand-ivory transition-colors duration-300"
                >
                  Schedule Your Complimentary Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Link>

                <Link
                  to="/coaching"
                  className="inline-flex items-center justify-center rounded-full border border-brand-cream/50 text-brand-cream font-semibold text-lg px-8 py-4 hover:bg-brand-cream/10 transition-colors duration-300"
                >
                  View Coaching
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
